import PropTypes from 'prop-types'

import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from '@/components/elements/card'
import { Form as RHFForm } from '@/components/elements/form'

// --------------------------------- SharedCardLayout ---------------------------------

/**
 * A layout for displaying a card with a title, description, and form.
 *
 * @param {object} props
 * @param {string} props.title - The title of the card.
 * @param {string} props.description - The description of the card.
 * @param {React.ReactNode} props.children - The form or other content to be displayed within the card.
 * @returns {React.ReactElement} The rendered card with title, description, and form.
 */
export const SharedCardLayout = ({ title, description, children }) => (
  <Card>
    <CardHeader>
      <CardTitle>{title}</CardTitle>
      {description && <CardDescription>{description}</CardDescription>}
    </CardHeader>
    {children}
  </Card>
)

SharedCardLayout.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  children: PropTypes.node.isRequired,
}

// --------------------------------- SharedCardLayout.Form ---------------------------------

/**
 * A form component for displaying a form within a card.
 *
 * @param {object} props
 * @param {object} props.formProps - The props to be passed to the form.
 * @param {function} props.onSubmit - The function to be called when the form is submitted.
 * @param {React.ReactNode} props.children - The content to be displayed within the form.
 * @returns {React.ReactElement} The rendered form within the card.
 */
SharedCardLayout.Form = function SharedCardForm({ formProps, onSubmit, children }) {
  return (
    <RHFForm {...formProps}>
      <form onSubmit={onSubmit}>{children}</form>
    </RHFForm>
  )
}

SharedCardLayout.Form.propTypes = {
  formProps: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
}

// --------------------------------- SharedCardLayout.Fields ---------------------------------

/**
 * A component for displaying fields within a card.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children - The content to be displayed within the fields.
 * @param {string} props.className - The class name to be applied to the fields.
 * @returns {React.ReactElement} The rendered fields within the card.
 */
SharedCardLayout.Fields = function SharedCardFields({ children, className }) {
  return <CardContent className={className}>{children}</CardContent>
}

SharedCardLayout.Fields.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
}

// --------------------------------- SharedCardLayout.Footer ---------------------------------

/**
 * A component for displaying a footer within a card.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children - The content to be displayed within the footer.
 * @returns {React.ReactElement} The rendered footer within the card.
 */

SharedCardLayout.Footer = function SharedCardFooter({ children }) {
  return <CardFooter>{children}</CardFooter>
}

SharedCardLayout.Footer.propTypes = {
  children: PropTypes.node.isRequired,
}
